import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import api from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';

export default function ResetPassword() {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/accounts/password-reset/confirm/', {
        uid,
        token,
        new_password: pw,
        new_password_confirm: pw2,
      });
      toast.success(res.data?.detail || 'Password reset. Please sign in.');
      navigate('/login');
    } catch (err) {
      if (!err.response) {
        toast.error('Cannot reach the server. Try again in a moment.');
      } else {
        const data = err.response.data;
        const msg = data?.detail
          || (data && typeof data === 'object' ? Object.values(data).flat().join(' ') : null)
          || `Reset failed (${err.response.status}).`;
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledWrapper $bgColor={settings?.login_bg_type === 'color' ? (settings?.login_bg_color || '#F8FAFB') : undefined}>
      <div className="container">
        <div className="card">
          <img src={settings?.header_logo || '/NIS ABJ LOGO.jpg'} alt="NIS FCT" className="logo" />
          <span className="title">Reset Password</span>
          <p className="subtitle">Choose a new password for your account.</p>

          <form onSubmit={handleSubmit}>
            <div className="inputBox">
              <input
                type={showPw ? 'text' : 'password'}
                required
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                placeholder=" "
              />
              <span>New Password</span>
            </div>
            <div className="inputBox">
              <input
                type={showPw ? 'text' : 'password'}
                required
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                minLength={8}
                autoComplete="new-password"
                placeholder=" "
              />
              <span>Confirm Password</span>
            </div>
            <label className="show-pw">
              <input
                type="checkbox"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
              />{' '}
              Show password
            </label>
            <button className="enter" type="submit" disabled={loading}>
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>

          <p className="back-link">
            <Link to="/login">&larr; Back to Sign In</Link>
          </p>
        </div>
      </div>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .container {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    background: ${(p) => p.$bgColor || '#F8FAFB'};
  }
  .card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    width: 420px;
    max-width: 95vw;
    background: #e3e3e3;
    box-shadow: 16px 16px 32px #c8c8c8, -16px -16px 32px #fefefe;
    border-radius: 12px;
    padding: 2.5rem 1.75rem;
  }
  .logo { width: auto; height: 80px; object-fit: contain; }
  .title {
    color: #0B5D1E;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: bold;
    font-size: 1.4rem;
    font-family: 'Playfair Display', serif;
  }
  .subtitle {
    color: #4b5563;
    font-size: 0.9rem;
    text-align: center;
    margin: -8px 0 0 0;
  }
  form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    width: 100%;
  }
  .inputBox {
    position: relative;
    width: 320px;
    max-width: 100%;
  }
  .inputBox input {
    width: 100%;
    padding: 12px;
    outline: none;
    border: none;
    color: #000;
    font-size: 1em;
    background: transparent;
    border-left: 2px solid #0B5D1E;
    border-bottom: 2px solid #0B5D1E;
    border-bottom-left-radius: 8px;
  }
  .inputBox span {
    margin-top: 5px;
    position: absolute;
    left: 0;
    transform: translateY(-4px);
    margin-left: 10px;
    padding: 10px;
    pointer-events: none;
    font-size: 12px;
    color: #374151;
    text-transform: uppercase;
    transition: 0.5s;
    letter-spacing: 3px;
    border-radius: 8px;
  }
  .inputBox input:focus ~ span,
  .inputBox input:not(:placeholder-shown) ~ span {
    transform: translateX(130px) translateY(-15px);
    font-size: 0.8em;
    padding: 5px 10px;
    background: #0B5D1E;
    letter-spacing: 0.2em;
    color: #fff;
  }
  .inputBox input:focus {
    border: 2px solid #0B5D1E;
    border-radius: 8px;
  }
  .show-pw {
    font-size: 0.85rem;
    color: #4b5563;
    align-self: flex-start;
    margin-left: calc(50% - 160px);
  }
  .enter {
    height: 48px;
    width: 200px;
    border-radius: 8px;
    border: 2px solid #0B5D1E;
    cursor: pointer;
    background-color: transparent;
    transition: 0.3s;
    text-transform: uppercase;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 2px;
    color: #0B5D1E;
  }
  .enter:hover { background-color: #0B5D1E; color: white; }
  .enter:disabled { opacity: 0.5; cursor: not-allowed; }
  .back-link { font-size: 0.875rem; }
  .back-link a {
    color: #0B5D1E;
    text-decoration: none;
    font-weight: 600;
  }
  .back-link a:hover { text-decoration: underline; }
`;
